/**
 * Items Service - Client for backend items API
 * Handles related items fetching
 */

import { supabase } from '../lib/supabase';

class ItemsService {
  /**
   * Parse Postgres composite type tuple string into object
   * Handles format: "(uuid,text,text,text,float,bool,bool,text)"
   * @private
   */
  parseTuple(tupleString) {
    if (!tupleString || typeof tupleString !== 'string') {
      return null;
    }

    // Remove outer parentheses
    const inner = tupleString.trim().replace(/^\(/, '').replace(/\)$/, '');
    
    // Split by comma, handling quoted strings
    const values = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < inner.length; i++) {
      const char = inner[i];
      
      if (char === '"' && (i === 0 || inner[i-1] !== '\\')) {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    // Add last value
    if (current) {
      values.push(current.trim());
    }
    
    // Remove quotes from values
    const cleanValues = values.map(v => {
      if (v.startsWith('"') && v.endsWith('"')) {
        return v.slice(1, -1).replace(/\\"/g, '"');
      }
      return v;
    });
    
    // Map to object structure matching new SQL function output
    const sourceType = cleanValues[3] || 'unknown';
    return {
      id: cleanValues[0] || null,
      display_title: cleanValues[1] || 'Untitled',
      title: cleanValues[1] || 'Untitled', // Backward compatibility
      thumbnail_url: cleanValues[2] || null,
      source_type: sourceType,
      sourceType: sourceType, // Backward compatibility (camelCase)
      similarity_score: cleanValues[4] ? Number(cleanValues[4]) : 0,
      has_thumbnail: cleanValues[5] === 't' || cleanValues[5] === 'true',
      is_text_item: cleanValues[6] === 't' || cleanValues[6] === 'true',
      url: cleanValues[7] || null,
    };
  }

  /**
   * Get related items for a given item ID
   * Calls Supabase RPC directly and parses composite type tuples
   * @param {string} itemId - Item ID
   * @param {number} limit - Maximum number of related items (default: 8)
   * @returns {Promise<{itemId: string, relatedItems: Array, count: number}>}
   */
  async getRelatedItems(itemId, limit = 8) {
    try {
      console.log('[RELATED] Fetching related items for:', itemId, 'limit:', limit);
      
      // Get current user
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        console.warn('[RELATED] No user session, returning empty results');
        return { itemId, relatedItems: [], count: 0 };
      }
      
      // Call RPC directly
      const { data, error } = await supabase.rpc('get_related_items', {
        p_item_id: itemId,
        p_user_id: session.user.id,
        p_limit: limit,
      });
      
      if (error) {
        console.error('[RELATED] RPC error:', error);
        throw error;
      }
      
      console.log('[RELATED] RPC returned:', data?.length || 0, 'results');
      console.log('[RELATED] First raw result:', data?.[0]);
      
      // Parse results
      let parsedResults = [];
      
      if (data && Array.isArray(data)) {
        parsedResults = data.map((item, index) => {
          // Check if item is a tuple string
          if (typeof item === 'string' && item.startsWith('(')) {
            console.log('[RELATED] Parsing tuple string:', item);
            return this.parseTuple(item);
          }
          // Check if item is already an object
          else if (typeof item === 'object' && item !== null) {
            const sourceType = item.source_type || 'unknown';
            return {
              id: item.id,
              display_title: item.display_title || item.title || 'Untitled',
              title: item.display_title || item.title || 'Untitled', // Backward compatibility
              thumbnail_url: item.thumbnail_url || null,
              source_type: sourceType,
              sourceType: sourceType, // Backward compatibility (camelCase)
              similarity_score: Number(item.similarity_score || 0),
              has_thumbnail: Boolean(item.has_thumbnail),
              is_text_item: Boolean(item.is_text_item),
              url: item.url || null,
            };
          }
          // Unknown format
          else {
            console.warn('[RELATED] Unknown format for item at index', index, ':', item);
            return null;
          }
        }).filter(Boolean); // Remove nulls
      }
      
      console.log('[RELATED] Parsed related items:', parsedResults);
      
      return {
        itemId,
        relatedItems: parsedResults,
        count: parsedResults.length,
      };
    } catch (error) {
      console.error('[RELATED] Error fetching related items:', error);
      
      // Return empty results on error
      return { itemId, relatedItems: [], count: 0 };
    }
  }
}

export default new ItemsService();

